import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  alpha,
  Box,
  Button,
  Card,
  Popover,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { generatedApi } from "src/store/api/generatedApi";

interface MixDifferencesSectionProps {
  tenantName: string;
  mixId: number;
  reversionTargetMixId: number;
}

interface RevertMixPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  mixId: number;
  reversionTargetMixId: number;
  tenantName: string;
  showDifferences: boolean;
}

const MixDifferencesSection = ({
  tenantName,
  mixId,
  reversionTargetMixId,
}: MixDifferencesSectionProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  //TODO: May want to use the QueryStatus enum from rtk query for this bit in
  // a switch statement.
  const { data: mixDifferences, isLoading } =
    generatedApi.endpoints.compareMixes.useQuery({
      tenantName,
      materialMetadataSetId: 1,
      srcMixId: mixId,
      tgtMixId: reversionTargetMixId,
    });

  return (
    <Box sx={{ borderRadius: 2, mx: 2, my: 1 }}>
      {isLoading || !mixDifferences ? (
        <Skeleton variant="rectangular" width="100%" height={100} />
      ) : (
        <Stack spacing={0.5} sx={{ px: 0.5 }}>
          {mixDifferences.differences.map((diff) => (
            <Card
              key={`${diff.basket_number}-${diff.material_name}`}
              variant="elevation"
              elevation={0}
              sx={{
                px: 0.5,
                py: 0.2,
                bgcolor: theme.palette.grey[50],
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "fontWeightMedium",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {diff.material_name}
                  </Typography>
                  {diff.addition_location === "basket" && (
                    <Typography
                      variant="overline"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.6rem",
                      }}
                    >
                      {t("basket")} {diff.basket_number}
                    </Typography>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ flexShrink: 0 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                    }}
                  >
                    {diff.source_mass.toFixed(2)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        mx: 0.25,
                      }}
                    >
                      →
                    </Typography>
                    {diff.target_mass.toFixed(2)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: "fontWeightSemiBold",
                      color:
                        diff.mass_difference > 0
                          ? theme.palette.success.main
                          : diff.mass_difference < 0
                            ? theme.palette.error.main
                            : "inherit",
                      fontSize: "0.75rem",
                    }}
                  >
                    ({diff.mass_difference > 0 ? "+" : ""}
                    {diff.mass_difference.toFixed(2)})
                  </Typography>
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export const RevertMixPopover = ({
  open,
  anchorEl,
  onClose,
  onConfirm,
  isLoading,
  mixId,
  reversionTargetMixId,
  tenantName,
  showDifferences,
}: RevertMixPopoverProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "center",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          sx: {
            overflowY: "auto",
            border: `1px solid ${theme.palette.secondary.light}`,
            borderRadius: 2,
            minWidth: 300,
            maxWidth: 350,
          },
        },
      }}
    >
      <Box sx={{ py: 1, px: 1.5 }}>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {t("confirmResetMix")}
        </Typography>
      </Box>

      <Box
        sx={{
          p: 1.5,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          color="secondary"
          variant="outlined"
          onClick={onClose}
          startIcon={<CancelRoundedIcon sx={{ fontSize: 14 }} />}
          size="small"
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isLoading}
          startIcon={
            <ReplayIcon
              sx={{
                fontSize: 14,
                ...(isLoading
                  ? {
                      animation: "spin 800ms infinite linear",
                      fill: theme.palette.primary.main,
                    }
                  : {}),
                "@keyframes spin": {
                  from: {
                    transform: "rotate(360deg)",
                  },
                  to: {
                    transform: "rotate(0deg)",
                  },
                },
              }}
            />
          }
          size="small"
        >
          {t("confirm")}
        </Button>
      </Box>

      {showDifferences ? (
        <MixDifferencesSection
          tenantName={tenantName}
          mixId={mixId}
          reversionTargetMixId={reversionTargetMixId}
        />
      ) : null}
    </Popover>
  );
};
